import React, { useState, useEffect, useRef, useMemo } from "react";
import DateRangePicker from "../utils/GenericDateRangeFilter/GenericDateRangeFilter";
import { useGetOrgInsights, usePlant, useUserDetail, useUserPermissions } from "../../services/hooks";
import { Navigate } from "react-router-dom";
import { ActionMenu, Button, Dropdown, GenericTable, tCell } from "../utils/index";
import { useGetVehicleMovementStatsMutation } from "../../api/vmsSlice";
import BarChart from "./BarChart";
import theme, { buttonClasses } from "../utils/theme";
import "./VehicleMovement.css";
import PieChart from "./PieChart";
import { capitalizeCamelCase } from "../../services/functions";
import GenericDownloadTable from "../utils/GenericDownloadTable";
import WarehouseTripInsightsTable from "./PlantSummary";

const VehicleMovementStats = () => {
  // Get today's date and date 6 days ago
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 6);

  const [dateRange, setDateRange] = useState({
    start: lastWeek,
    end: today,
  });
  const [filters, setFilters] = useState({ plantIds: [] });
  const [barChartData, setBarChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userDetail } = useUserDetail();
  const { userPermissions, isFetched } = useUserPermissions();
  const [orgStats, setOrgStats]= useState([])
  const { plantOptions } = usePlant();

  const [getVehicleMovementStats, { isLoading: statsLoading }] =useGetVehicleMovementStatsMutation();
  const {orgInsightsData, count, message, isLoading, getOrgInsights}= useGetOrgInsights()
  const selectedPlants = plantOptions.filter((opt) =>filters.plantIds.includes(opt.value));

  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
  // API CALLING
  const fetchData = async (plantIds, startDate, endDate) => {
    try {
      setLoading(true);
      const result = await getVehicleMovementStats({plantIds, start: startDate.toISOString(), end: endDate.toISOString(),}).unwrap();      
      const orgInsightResults= await getOrgInsights({ startTime: startOfDay.toISOString(), version:'v2',
      endTime: endOfDay.toISOString()}).unwrap()
      setOrgStats(orgInsightResults?.data)      
      const validateBarData = validateHasAllDay(result.barChartData, startDate, endDate, ["totalGateIn", "totalWeighbridgeIn", "totalWeighbridgeOut", "totalGateOut"]);  
      setBarChartData(validateBarData);
      setPieChartData(result.pieChartData || []);
      setDateRange({ start: startDate, end: endDate });
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false); // stop loading AFTER all state is set
    }
  };
  useEffect(() => {
    if (plantOptions.length > 0 && filters.plantIds.length === 0) {
      const allPlantIds = plantOptions.map((opt) => opt.value);
      setFilters({ plantIds: allPlantIds });

      // Call API only for first render
      // BY DEFAULT LAST ONE WEEK DATA
      const today = new Date();
      const lastWeek = new Date();
      lastWeek.setDate(today.getDate() - 6);

      fetchData(allPlantIds, lastWeek, today)
    }
  }, [plantOptions]);

  const handleDateRangeSelect = (range) => {
    const startDate = new Date(range.startDate);
    startDate.setHours(0, 0, 0, 0); // Set to start of day
    const endDate = new Date(range.endDate);
    endDate.setHours(23, 59, 59, 999); // Set to end of day
    
    setDateRange({
      start: startDate,
      end: endDate,
    });
  };

  const handlePlantChange = (selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      plantIds: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
    }));
  };
  const handleResetFilters = () => {
    // default last 7 days
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 6);
    const allPlantIds = plantOptions.map((opt) => opt.value);
    setFilters({ plantIds: [] }); // clears all filters
    setDateRange({ start: lastWeek, end: today });
    fetchData(allPlantIds, lastWeek, today);
  };

  const handleFilterChange = async () => {
    if (!dateRange.start || !dateRange.end || !filters.plantIds.length) {
      return;
    }
    fetchData(filters.plantIds, dateRange?.start, dateRange?.end)
  };
  const validateHasAllDay = (data, start, end, keyArr) => {  
    const generateDateRange = (startDate, endDate) => {
      // Convert to UTC dates to avoid timezone issues
      const startUTC = Date.UTC(
        startDate.getFullYear(), 
        startDate.getMonth(), 
        startDate.getDate()
      );
      
      const endUTC = Date.UTC(
        endDate.getFullYear(), 
        endDate.getMonth(), 
        endDate.getDate()
      );
      
      const dates = [];
      const oneDay = 24 * 60 * 60 * 1000; // milliseconds in one day
      
      for (let dateUTC = startUTC; dateUTC <= endUTC; dateUTC += oneDay) {
        const date = new Date(dateUTC);
        dates.push(date.toISOString().split("T")[0]);
      }
      
      return dates;
    };
    
    const allDates = generateDateRange(start, end);
    const dataMap = data?.reduce((map, item) => {
      map[item._id] = { ...item };
      return map;
    }, {});
    
    return allDates.map((date) => {
      let res = { _id: date };
      for (let key of keyArr) {
        res[key] = dataMap[date]?.[key] || 0;
      }
      return res;
    });
  };

  const getPieChartDataForCategory = (category) => {
    const categoryData = pieChartData.find(
      (item) => item._id === category
    );
    if (!categoryData) return [];
    return [
      { name: "Camera", value: categoryData.totalCamera || 0 },
      { name: "RFID", value: categoryData.totalRfid || 0 },
      { name: "Manual", value: categoryData.totalManual || 0 },
    ];
  };

    const excelHeaders = useMemo(() => [
      "Warehouse Name",
      "Total",
      "Total Ongoing",
      "Ongoing % Complete",
      "Ongoing Trips vs Yesterday",
      "Total Completed",
      "Completed % Complete",
      "Completed Trips vs Yesterday",
    ], []);
    const excelRows = useMemo(() => {
      const today = orgStats?.today || [];
      const yesterday = orgStats?.yesterday || [];

      const yesterdayMap = {};
      yesterday.forEach(d => {
        if (d?.plantId) yesterdayMap[d.plantId] = d;
      });

      return today.map(d => {
        const y = yesterdayMap[d.plantId] || {};
        return [
          d.plantName,
          d.total,
          d.ongoing,
          d.total ? ((d.ongoing / d.total) * 100).toFixed(1) : 0,
          d.ongoing - (y.ongoing ?? 0),
          d.completed,
          d.total ? ((d.completed / d.total) * 100).toFixed(1) : 0,
          d.completed - (y.completed ?? 0),
        ];
      });
    }, [orgStats]);

  if (isFetched && userPermissions && Object.keys(userPermissions).length && !userPermissions?.viewVehicleMovementStats?.value)
    return <Navigate to="/not-authorized" replace />;

  return (
    <div className="new-dashboard-container">
      <div className="contentpanel">
        <div className="dashboard-row cust-row flex-algn-cent">
          <div className="cust-col-5" id="dash">
            <h1 className="title">Dashboard</h1>
          </div>
          <div className="cust-col-5 flex-jc-end"></div>
        </div>
        <hr className="bgg" />
      </div>

      <div className="filter-section" style={{ display: "flex", gap: "1rem", marginTop:"1rem", flexWrap:"wrap" }}>
        <div style={{maxWidth:"260px", minWidth:"260px"}}>
          <Dropdown
            id="plantFilter"
            name="plantId"
            label="Warehouses"
            onChange={handlePlantChange}
            options={plantOptions}
            value={selectedPlants}
            multiple
          />
        </div>
        <div style={{maxWidth:"260px", minWidth:"270px", marginTop:"7px"}}>
          <DateRangePicker  
            onDateRangeSelect={handleDateRangeSelect}
            initialStartDate={dateRange.start} 
            initialEndDate= {dateRange.end}
          />
        </div>
        <div style={{ minWidth: "250px", display:"flex", gap: "1rem", maxHeight:"41px" }}> 
          <Button
            text="Reset"
            onClick={handleResetFilters}
            style={buttonClasses.lynkitOrangeEmpty}
          />
          <Button
            text={loading  ? "Loading..." : "Apply Filters"}
            onClick={handleFilterChange}
            disabled={!filters.plantIds.length ||!dateRange.start ||!dateRange.end ||statsLoading}
            style={buttonClasses.lynkitOrangeFill}
          />  
        </div>
        
      </div>
      <div>
            <div
              className="vehicleMovementContainer"
              style={{
                borderRadius: "8px",
                padding: "15px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <div className="vehicleMovementHeading">
                Vehicle Movement
              </div>
              {loading && <BarChartSkeleton barGroupCount={7} />}
              {!loading  && barChartData.length > 0 && (
                <BarChart dataSet={barChartData} chartWidth="100%" />
              )}
            </div>
            {loading && <PieChartSkeleton barGroupCount={7} />}
            {!loading  && pieChartData.length > 0 && (
              <div className="eventHeading"
                style={{
                  borderRadius: "8px",
                  padding: "15px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginTop:"1.5rem"
                }}
              >
                Events
                <div
                  className="pie-charts-container"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px",
                    marginTop: "20px",
                  }}
                >
                  {pieChartData.map((categoryData) => (
                    <div
                      key={categoryData._id}
                      className="pie-chart-card"
                      style={{
                        flex: "1",
                        minWidth: "300px",
                        maxWidth: "413px",
                        borderRadius: "8px",
                        padding: "15px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        border: "1px solid var(--input-border-color)"
                      }}
                    >
                      <div className="eventSubHeading">{capitalizeCamelCase(categoryData._id)}</div>
                      <PieChart dataSet={getPieChartDataForCategory(categoryData._id)} chartWidth="100%"/>
                    </div>
                  ))}
                </div>
              </div>
            )}

        {userDetail?.userRoleHierarchy == 0 && !isLoading && orgStats && (
          <div style={{borderRadius: "8px", padding: "15px 9px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", marginTop:"1.5rem"}}>
          <div className="dashboard-row cust-row flex-algn-cent">
            <div className="cust-col-5" id="dash" style={{width:"100%", display:"flex", justifyContent:"space-between"}}>
              <h1 className="title">Warehouse Trips Summary</h1>
            </div>
            <div style={{minWidth:"120px"}}>
              <GenericDownloadTable
                headers={excelHeaders}
                rows={excelRows}
                fileName="Warehouse_Trip_Insights"
              />
            </div>
          </div>
          <WarehouseTripInsightsTable data={orgStats}/>
          </div>
        )}    
      </div>
    </div>
  );
};

export default VehicleMovementStats;


const BAR_HEIGHTS = [
  [65, 50, 40, 55],
  [80, 70, 55, 45],
  [45, 60, 75, 50],
  [90, 55, 65, 80],
  [60, 85, 50, 70],
  [75, 45, 85, 60],
  [50, 75, 60, 90],
];

 const BarChartSkeleton = ({ barGroupCount = 7 }) => {
  const groups = BAR_HEIGHTS.slice(0, barGroupCount);

  return (
    <div className="bar-chart-skeleton">
      {/* Legend placeholders */}
      <div className="bar-skeleton-header">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bar-skeleton-legend skeleton-shimmer" />
        ))}
      </div>

      {/* Bar groups */}
      <div className="bar-skeleton-body">
        {groups.map((heights, gi) => (
          <div key={gi} className="bar-skeleton-group">
            {heights.map((h, bi) => (
              <div
                key={bi}
                className="bar-skeleton-bar"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* X-axis date labels */}
      <div className="bar-skeleton-x-labels">
        {groups.map((_, i) => (
          <div key={i} className="bar-skeleton-x-label">
            <div className="skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
  );
};

 const PieChartSkeleton = ({ count = 4 }) => {
  const titles = ["Gate In", "Weighbridge In", "Weighbridge Out", "Gate Out"];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "20px",
        marginTop: "20px",
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="pie-chart-skeleton-card">
          {/* Title placeholder */}
          <div className="pie-skeleton-title skeleton-shimmer" />

          <div className="pie-skeleton-body">
            {/* Donut circle */}
            <div className="pie-skeleton-circle-wrapper">
              <div className="pie-skeleton-circle" />
            </div>

            {/* Legend items */}
            <div className="pie-skeleton-legends">
              {["blue", "green", "orange"].map((color) => (
                <div key={color} className="pie-skeleton-legend-item">
                  <div className={`pie-skeleton-legend-dot ${color}`} />
                  <div className="pie-skeleton-legend-text skeleton-shimmer" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};